"use client";

import { cn } from "@/lib/utils";
import { RotateCcw } from "lucide-react";
import React from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import Select, {
  components,
  ControlProps,
  GroupBase,
  IndicatorsContainerProps,
  MenuListProps,
  Props,
  StylesConfig,
} from "react-select";
import { Button } from "../button/Button";
import { useTranslatedPlaceholder } from "@/hooks/useTranslatedPlaceholder/useTranslatedPlacehoder";

export type SelectProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Props<Option, IsMulti, Group> & {
  error?: string;
  label?: string;
  register?: UseFormRegisterReturn;
  size?: "sm" | "md";
};

/**
 * Hook to dynamically detect and update translated placeholder text.
 * Useful for WOVN or client-side translation integrations.
 */
// Move Control component outside - accept searchIconCustom as prop
const CustomControl = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  searchIconCustom,
  ...controlProps
}: ControlProps<Option, IsMulti, Group> & {
  searchIconCustom?: JSX.Element;
}) => {
  return (
    <components.Control {...controlProps}>
      {searchIconCustom && (
        <div className="flex items-center mr-2">
          {searchIconCustom}
        </div>
      )}
      {children}
    </components.Control>
  );
};

// Move MenuList component outside - accept props for button
const CustomMenuList = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  showAllButton,
  onShowAll,
  ...props
}: MenuListProps<Option, IsMulti, Group> & {
  showAllButton?: boolean;
  onShowAll?: () => void;
}) => {
  return (
    <div>
      <div className="relative max-h-[300px] overflow-y-auto pt-2 pb-2">
        {props.children}
      </div>
      {showAllButton && (
        <div className="flex justify-center border-t border-gray-200 px-2 py-2">
          <Button
            onClick={onShowAll}
            hierarchy={"ghost"}
            variant={"neutral"}
            className="flex gap-1"
          >
            <RotateCcw className="h-3 w-3" />
            すべて表示
          </Button>
        </div>
      )}
    </div>
  );
};

// Move IndicatorsContainer outside - accept iconCustom as prop
const CustomIndicatorsContainer = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>({
  iconCustom,
  ...props
}: IndicatorsContainerProps<Option, IsMulti, Group> & {
  iconCustom?: JSX.Element;
}) => {
  const childrenArray = React.Children.toArray(props.children);
  const filteredChildren = iconCustom
    ? childrenArray.filter(
        (child) =>
          React.isValidElement(child) &&
          child.type !== components.DropdownIndicator,
      )
    : childrenArray;

  return (
    <components.IndicatorsContainer {...props}>
      {iconCustom}
      {filteredChildren}
    </components.IndicatorsContainer>
  );
};
// Top-level wrappers that read extra data from selectProps forwarded into Select
const MenuListWrapper = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  menuProps: MenuListProps<Option, IsMulti, Group> & {
    selectProps?: Props<Option, IsMulti, Group> & {
      showAllButton?: boolean;
      onShowAll?: () => void;
    };
  },
) => {
  const showAllButton = menuProps.selectProps?.showAllButton;
  const onShowAll = menuProps.selectProps?.onShowAll as
    | (() => void)
    | undefined;

  return (
    <CustomMenuList
      {...menuProps}
      showAllButton={showAllButton}
      onShowAll={onShowAll}
    />
  );
};

const IndicatorsContainerWrapper = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  indicatorProps: IndicatorsContainerProps<Option, IsMulti, Group> & {
    selectProps?: Props<Option, IsMulti, Group> & { iconCustom?: JSX.Element };
  },
) => {
  const iconCustom = indicatorProps.selectProps?.iconCustom;
  return (
    <CustomIndicatorsContainer {...indicatorProps} iconCustom={iconCustom} />
  );
};

const ControlWrapper = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  controlProps: ControlProps<Option, IsMulti, Group> & {
    selectProps?: Props<Option, IsMulti, Group> & {
      searchIconCustom?: JSX.Element;
    };
  },
) => {
  const searchIcon = controlProps.selectProps?.searchIconCustom;
  return <CustomControl {...controlProps} searchIconCustom={searchIcon} />;
};

/**
 * CustomSelect
 *
 * A flexible wrapper around `react-select` supporting:
 * - Custom icons, placeholders, and translated labels
 * - Multi/single select with optional "Show All" button
 * - Form integration via `react-hook-form`
 * - Error styling and size variants
 */
export const CustomSelect = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectProps<Option, IsMulti, Group> & {
    error?: string;
    label?: string;
    register?: UseFormRegisterReturn;
    size?: "sm" | "md";
    containerClassName?: string;
    iconCustom?: JSX.Element;
    searchIconCustom?: JSX.Element;
    labelClassName?: string;
    styles?: StylesConfig<Option, IsMulti, Group>;
    required?: boolean;
    showAllButton?: boolean;
    onShowAll?: () => void;
    noOptionsMsg?: string;
    hasError?: boolean;
  },
) => {
  const {
    label,
    error,
    size = "md",
    isDisabled,
    isMulti,
    className,
    iconCustom,
    containerClassName,
    labelClassName = "",
    searchIconCustom,
    styles: customStyles,
    required,
    showAllButton,
    onShowAll,
    placeholder,
    noOptionsMsg = "オプションなし",
    hasError,
    ...restProps
  } = props;

  const effectivePlaceholder = useTranslatedPlaceholder(placeholder as string);

  /**
   * Default style overrides for react-select.
   * Adjusts borders, spacing, typography, and focus states.
   */
  const selectStyles: StylesConfig<Option, IsMulti, Group> = {
    control: (base, state) => ({
      ...base,
      width: "100%",
      minHeight: size === "md" ? "24px" : "20px",
      padding: size === "md" ? "7px 12px" : "5px 10px",
      border:
        !error && !hasError
          ? "1px solid var(--stroke-gray-normal)"
          : "1px solid var(--stroke-base-red)",
      boxShadow: "none",
      borderRadius:
        size === "md" ? "var(--radius-input-md)" : "var(--radius-input-sm)",
      fontWeight: "400",
      color: state.isDisabled
        ? "var(--text-gray-light)"
        : "var(--fill-gray-normal)",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      ...(state.isDisabled
        ? {
            "&:disable": {
              border: "1px solid var(--stroke-gray-light)",
              color: "var(--text-gray-lighter)",
            },
          }
        : {
            "&:focus-within": {
              backgroundColor: "#FFF",
              border:
                !error && !hasError
                  ? "1px solid var(--stroke-base-primary)"
                  : "1px solid var(--stroke-base-red)",
            },
            "&:hover": {
              border:
                !error && !hasError
                  ? "1px solid var(--stroke-base-primary)"
                  : "1px solid var(--stroke-base-red)",
            },
            "&:focus": { border: "1px solid var(--stroke-base-primary)" },
            "&:active": {
              boxShadow: "var(--shadow-input-active)",
            },
          }),
    }),
    input: (base, _state) => ({
      ...base,
      padding: 0,
      fontSize: size === "md" ? "16px" : "14px",
      lineHeight: size === "md" ? "20px" : "16px",
    }),
    indicatorSeparator: () => ({
      display: "none",
      height: "16px",
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      padding: 0,
      color: state.isDisabled
        ? "var(--text-gray-light)"
        : "var(--fill-gray-normal)",
      "&:hover": {
        color: state.isDisabled
          ? "var(--text-gray-light)"
          : "var(--fill-gray-normal)",
      },
    }),
    valueContainer: (base, _state) => ({
      ...base,
      padding: 0,
    }),
    singleValue: (base, _state) => ({
      ...base,
      padding: 0,
      fontSize: size === "md" ? "16px" : "14px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: size === "md" ? "16px" : "14px",
      fontWeight: "400",
      backgroundColor: state.isDisabled
        ? "var(--fill-gray-lighter)"
        : base.backgroundColor,
      cursor: state.isDisabled ? "not-allowed" : "pointer",
    }),
    placeholder: (base, _state) => ({
      ...base,
      fontSize: size === "md" ? "16px" : "14px",
    }),
    multiValue: (base, _state) => ({
      ...base,
      borderRadius:
        size === "md" ? "var(--radius-input-md)" : "var(--radius-input-sm)",
      color: "var(--text-gray-stronger)",
      backgroundColor: "var(--fill-gray-lighter)",
      cursor: "pointer",
      "&:hover, &:focus-within": {
        backgroundColor: isDisabled ? "" : "var(--fill-gray-light)",
      },
    }),
    multiValueLabel: (base, _state) => ({
      ...base,
      borderTopLeftRadius:
        size === "md" ? "var(--radius-input-md)" : "var(--radius-input-sm)",
      borderBottomLeftRadius:
        size === "md" ? "var(--radius-input-md)" : "var(--radius-input-sm)",
      color: "var(--text-gray-stronger)",
      padding: size === "md" ? "4px 6px" : "4px 2px",
      backgroundColor: "transparent",
      cursor: "pointer",
    }),
    multiValueRemove: (base, _state) => ({
      ...base,
      borderTopRightRadius:
        size === "md" ? "var(--radius-input-md)" : "var(--radius-input-sm)",
      borderBottomRightRadius:
        size === "md" ? "var(--radius-input-md)" : "var(--radius-input-sm)",
      cursor: "pointer",
      marginLeft: "-6px",
      color: "var(--fill-gray-normal)",
      "&:hover": {
        backgroundColor: "transparent",
        color: "var(--fill-gray-normal)",
      },
    }),
    clearIndicator: (base, _state) => ({
      ...base,
      cursor: "pointer",
    }),
    menu: (base, _state) => ({
      ...base,
      zIndex: 10,
    }),
  };

  // Create properly typed component wrappers

  return (
    <div className={cn("flex flex-col gap-1", containerClassName)}>
      {label && (
        <label
          htmlFor={restProps.inputId}
        aria-label={label || effectivePlaceholder || "Select option"}
          className={`${
            size === "md" ? "text-base" : "text-sm"
          } mr-2 font-medium text-[var(--text-gray-stronger)] ${labelClassName}`}
        >
          {label}{" "}
          {!!required && (
            <span className="text-[var(--stroke-base-red)]">*</span>
          )}
        </label>
      )}
      <Select<Option, IsMulti, Group>
        {...restProps}
        noOptionsMessage={() => noOptionsMsg}
        placeholder={effectivePlaceholder}
        classNamePrefix="custom-select"
        className={cn(className)}
        isMulti={isMulti}
        required={required}
        styles={{ ...selectStyles, ...customStyles }}
        isDisabled={isDisabled}
        aria-label={label || effectivePlaceholder || "Select option"}
        {...(searchIconCustom
          ? ({ searchIconCustom } as Partial<
              Props<Option, IsMulti, Group> & { searchIconCustom?: JSX.Element }
            >)
          : {})}
        components={{
          IndicatorsContainer: IndicatorsContainerWrapper,
          ...(showAllButton ? { MenuList: MenuListWrapper } : {}),
          ...(searchIconCustom ? { Control: ControlWrapper } : {}),
        }}
      />
      {error && <p className="text-xs text-(--stroke-base-red)">{error}</p>}
    </div>
  );
};
